import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import Select, { ActionMeta, MultiValue, SingleValue } from 'react-select';
import { CountryModel } from '@app/Models';

export interface ISelectArguments {
    value: string | null;
}
export type SelectCallback = (args: ISelectArguments) => void;

interface ISelectFilterProps<TOption> {
    title: string;
    placeholder?: string;
    labelKey?: keyof TOption;
    valueKey?: keyof TOption;
    value?: string | null;
    multi?: boolean;
    disableClearable?: boolean;
    closeOnSelect?: boolean;
    options: (TOption | CountryModel)[];
    onSelect: SelectCallback;
    className?: string;
    disabled?: boolean;
}

class SelectStore {
    @observable selectedValue: string = '';
    @observable defaultValue: string = '';
    @observable closed: boolean = true;

    @action
    updateDefault(newValue?: string | null) {
        this.defaultValue = newValue ? newValue : '';
        this.selectedValue = this.defaultValue;
    }
}

@observer
export default class SelectFilter<T extends {}> extends React.Component<ISelectFilterProps<T>, {}> {
    store: SelectStore = new SelectStore();

    componentDidMount() {
        this.store.updateDefault(this.props.value);
    }

    componentWillReceiveProps(newProps: ISelectFilterProps<T>) {
        this.store.updateDefault(newProps.value);
    }

    render() {
        const { options, onSelect, multi, disableClearable, closeOnSelect, placeholder = 'None' } = this.props; 
        return (
            <Select
                placeholder={placeholder}
                closeMenuOnSelect={closeOnSelect || !multi}
                onChange={(selected: SingleValue<CountryModel | T> | MultiValue<CountryModel | T>, _: ActionMeta<CountryModel | T>) => {
                    let selectedValue: string | string[] = '';     
                    if(selected){
                        if(multi || Array.isArray(selected)){
                            const multiSelected = selected as MultiValue<T | CountryModel>;
                            selectedValue = multiSelected.map(o => this.getValueString(o));
                        }else{
                            const singleSelected = selected as T | CountryModel;
                            if (singleSelected){
                                selectedValue = this.getValueString(singleSelected);                    
                            }
                        }
                    }
                    this.store.selectedValue = String(selectedValue);

                    if (!this.store.selectedValue || !multi || (this.store.selectedValue !== this.store.defaultValue && this.store.closed)) {
                        onSelect({value: this.store.selectedValue});
                    }
                }}
                onMenuOpen={multi ? this.openHandler : undefined}
                onMenuClose={multi ? this.closeHandler : undefined}
                getOptionLabel={option => this.getLabelString(option)}
                getOptionValue={option => this.getValueString(option)}
                isMulti={multi}
                isClearable={!disableClearable}
                options={options}
                hideSelectedOptions={false}
                value={options && options.filter( option => this.getValueString(option) === this.store.selectedValue)}
                classNamePrefix="select-filter"
                className="select-filter-container"
                isDisabled={this.props.disabled}
                />
            
        );
    }

    getLabelString(option: T | CountryModel): string{
        let { labelKey } = this.props;

        if (labelKey){
            const o = option as T;
            if (o){
                return '' + o[labelKey];
            }
        }

        const o = option as CountryModel;
        if (o){
            return o.label;
        }

        return '';
    }

    getValueString(option: T | CountryModel): string{
        let { valueKey } = this.props;

        if (valueKey){
            const o = option as T;
            if (o){
                return '' + o[valueKey];
            }
        }

        const o = option as CountryModel;
        if (o){
            return o.value;
        }

        return '';
    }

    closeHandler = () => {        
        const { onSelect } = this.props;
        const { selectedValue, defaultValue } = this.store;
        this.store.closed = true;
        if (selectedValue && selectedValue !== defaultValue) {
            onSelect({
                value: selectedValue
            });
        } else if (!selectedValue && defaultValue) {
            onSelect({
                value: null
            });
        }
    };
    
    openHandler = () =>{
        this.store.closed = false;
    };
}